import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  const checkPaymentStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/payments/status/${sessionId}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setPaymentDetails(data);
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('error');
    }
  }, [sessionId]);

  useEffect(() => {
    checkPaymentStatus();
  }, [checkPaymentStatus]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <XCircle size={64} weight="fill" className="text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Payment Verification Failed
          </h1>
          <p className="text-gray-600 mb-6">
            We couldn&apos;t verify your payment. Please contact support if you were charged.
          </p>
          <Link
            to="/pricing"
            className="inline-block bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors"
          >
            Return to Pricing
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <CheckCircle size={64} weight="fill" className="text-green-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Payment Successful!
        </h1>
        <p className="text-gray-600 mb-6">
          Thank you for your purchase. Your account has been upgraded.
        </p>

        {paymentDetails && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <h2 className="font-semibold text-gray-900 mb-2">Order Details</h2>
            <div className="space-y-1 text-sm text-gray-600">
              <p>
                <span className="font-medium">Package:</span>{' '}
                {paymentDetails.package_type}
              </p>
              <p>
                <span className="font-medium">Amount:</span> $
                {(paymentDetails.amount / 100).toFixed(2)}
              </p>
              <p>
                <span className="font-medium">Transaction ID:</span>{' '}
                {paymentDetails.transaction_id}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <Link
            to="/"
            className="block w-full bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors"
          >
            Go to Dashboard
          </Link>
          <Link
            to="/pricing"
            className="block w-full text-green-600 hover:text-green-700 transition-colors"
          >
            View Other Plans
          </Link>
        </div>
      </div>
    </div>
  );
}
